import React, { useState } from 'react';

import {
  Button,
  IconButton,
  Box,
  Typography,
  Grid,
  Toolbar,
  Divider,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { createProvider, updateProvider } from '../../../../actions/providers';
import PROVIDER_TYPES from '../../providerTypes';
import AzureTenantIdField from './AzureTenantIdField';
import AzureClientIdField from './AzureClientIdField';
import AzureClientSecretField from './AzureClientSecretField';
import AzureResourceGroupField from './AzureResourceGroupField';
import AzureSubscriptionField from './AzureSubscriptionField';
import { getResourcesDashboardPath } from '@/util/routePaths';

const provider_type = PROVIDER_TYPES.AZURE;

export default function AzureProviderForm({ provider }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [pristine, setPristine] = useState(false);
  const [payment_mode] = useState(provider?.payment_mode || 'credits');
  const [tenant_id, setTenantId] = useState(provider?.credentials?.tenant || '');
  const [tenant_id_error, setTenantIdError] = useState(false);
  const [client_id, setClientId] = useState(provider?.credentials?.id || '');
  const [client_id_error, setClientIdError] = useState(false);
  const [client_secret, setClientSecret] = useState(provider?.credentials?.secret || '');
  const [client_secret_error, setClientSecretError] = useState(false);
  const [resource_group, setResourceGroup] = useState(provider?.credentials?.resource_group || '');
  const [resource_group_error, setResourceGroupError] = useState(false);
  const [subscription_id, setSubscriptionId] = useState(provider?.credentials?.subscription || '');
  const [subscription_id_error, setSubscriptionIdError] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const credentials = Object.fromEntries(
      Object.entries({
        id: client_id,
        secret: client_secret,
        tenant: tenant_id,
        resource_group: resource_group,
        subscription: subscription_id,
      }).filter(([k, v]) => v !== undefined && v !== '')
    );
    const payload = Object.fromEntries(
      Object.entries({
        payment_mode,
        credentials,
        managed_deployment: true,
      }).filter(([k, v]) => v !== undefined && v !== '')
    );
    if (provider) {
      await dispatch(updateProvider(provider.provider_uuid, payload));
    } else {
      await dispatch(createProvider({ ...payload, type: provider_type.value }));
    }

    navigate('/resources');
  };

  return (
    <Paper sx={{ display: 'flex', width: '100%' }}>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => navigate(getResourcesDashboardPath())} disabled={loading}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>{provider_type?.label}</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Box padding='15px' sx={{ maxWidth: 400 }}>
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Typography variant='h6'>Credentials</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <AzureTenantIdField
                  value={tenant_id}
                  error={tenant_id_error}
                  setValue={setTenantId}
                  setError={setTenantIdError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <AzureClientIdField
                  value={client_id}
                  error={client_id_error}
                  setValue={setClientId}
                  setError={setClientIdError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <AzureClientSecretField
                  value={client_secret}
                  error={client_secret_error}
                  setValue={setClientSecret}
                  setError={setClientSecretError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <AzureResourceGroupField
                  value={resource_group}
                  error={resource_group_error}
                  setValue={setResourceGroup}
                  setError={setResourceGroupError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <AzureSubscriptionField
                  value={subscription_id}
                  error={subscription_id_error}
                  setValue={setSubscriptionId}
                  setError={setSubscriptionIdError}
                  setChanged={() => setPristine(false)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Divider />
          <Toolbar>
            <Stack spacing={2} direction='row'>
              <Button
                variant='contained'
                color='brand'
                onClick={handleSubmit}
                disabled={
                  pristine ||
                  !Boolean(tenant_id) ||
                  !Boolean(client_id) ||
                  !Boolean(resource_group) ||
                  !Boolean(subscription_id) ||
                  (!provider && !Boolean(client_secret)) ||
                  tenant_id_error ||
                  client_id_error ||
                  client_secret_error ||
                  resource_group_error ||
                  subscription_id_error ||
                  loading
                }
                startIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                Submit
              </Button>

              <Button
                variant='outlined'
                onClick={() => navigate(getResourcesDashboardPath())}
                disabled={loading}
              >
                Cancel
              </Button>
            </Stack>
          </Toolbar>
        </Grid>
      </Grid>
    </Paper>
  );
}

